import type {QUERY_FIELDS} from '@github-ui/query-builder/constants/queries'
import {QUERIES} from '@github-ui/query-builder/constants/queries'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {ROUTE_SUFFIXES} from './constants/route-suffixes'
import {VIEW_IDS} from './constants/view-constants'

const ownerWithOptionalUnderscore = '([a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)?(-[a-zA-Z0-9]+|-)*)'
export const issuesAtRepoIndexUrl = new RegExp(`^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/issues`)
export const issuesAtLabelIndexUrl = new RegExp(`^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/labels`)
export const issuesAtLabelShowUrl = new RegExp(
  `^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/labels/([._a-zA-Z0-9-]+)`,
)
export const issuesAtMilestoneShowUrl = new RegExp(
  `^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/milestone/([0-9]+)`,
)

export const searchUrl = ({viewId, query}: {viewId?: string; query?: string}) => {
  let newQuery = ''

  if (viewId !== VIEW_IDS.repository) {
    if (viewId && viewId !== VIEW_IDS.empty) {
      if (ROUTE_SUFFIXES[viewId]) {
        newQuery = `/${ROUTE_SUFFIXES[viewId]}`
      } else {
        newQuery = `/${viewId}`
      }
    } else {
      if (query === QUERIES.assignedToMe) {
        return `/issues/${ROUTE_SUFFIXES.assigned}`
      } else if (query === QUERIES.mentioned) {
        return `/issues/${ROUTE_SUFFIXES.mentioned}`
      } else if (query === QUERIES.createdByMe) {
        return `/issues/${ROUTE_SUFFIXES.createdByMe}`
      } else if (query === QUERIES.recentActivity) {
        return `/issues/${ROUTE_SUFFIXES.recentActivity}`
      }
    }
  }

  if (query !== undefined && query.trim() !== '') {
    newQuery = `${newQuery}?q=${encodeURIComponent(query)}`
  }

  if (viewId === VIEW_IDS.repository) {
    // this is when hyperlist is running at the repository level
    const path = ssrSafeLocation?.pathname
    let match = path.match(issuesAtRepoIndexUrl)

    if (match) {
      return path + newQuery
    }
    match =
      path.match(issuesAtLabelIndexUrl) || path.match(issuesAtLabelShowUrl) || path.match(issuesAtMilestoneShowUrl)
    if (match) {
      return `/${match[1]}/${match[3]}/issues${newQuery}`
    }
  }

  return `/issues${newQuery}`
}

function formatFieldValue(value: string): string {
  return value.indexOf(' ') > -1 ? `"${value}"` : value
}

/**
 * Removes all instances of the given field from the query, then adds the field with the new value
 * @param query - The search query string to modify
 * @param field - The field name to replace (eg. 'label', 'assignee', 'is')
 * @param value - The new value for the field
 * @returns The modified query string, or undefined if the original query was empty
 * @example
 * replaceInQuery('is:open label:bug label:feature', 'label', 'enhancement')
 * // Returns: 'is:open label:enhancement'
 */
export function replaceInQuery(query: string, field: keyof typeof QUERY_FIELDS, value: string) {
  if (!query) {
    return undefined
  }
  // Matches `field:` followed by quoted values or unquoted values (eg. `label:bug` or `label:"needs review"`)
  const regex = new RegExp(`(?:^|\\s)${field}:(?:\\"[^\\"]*\\"|\\S+)`, 'g')
  const newQuery = query.replaceAll(regex, '').replace(/^\s+/, '')
  const newValue = formatFieldValue(value)

  return `${newQuery.trim()} ${field}:${newValue}`
}

/**
 * Removes any existing instance of the specific field:value combination from the query, then appends it to the end
 * @param query - The search query string to modify
 * @param field - The field name to add (eg. 'label', 'assignee', 'is')
 * @param value - The value for the field
 * @returns The modified query string with the field:value moved to the end, or just the field:value if the original query was empty
 * @example
 * addToQuery('is:open', 'label', 'bug')
 * // Returns: 'is:open label:bug'
 *
 * addToQuery('is:open label:bug author:monalisa', 'label', 'bug')
 * // Returns: 'is:open author:monalisa label:bug'
 */
export function addToQuery(query: string, field: keyof typeof QUERY_FIELDS, value: string) {
  if (!query) {
    return `${field}:${value}`
  }

  const targetValue = formatFieldValue(value)
  // Escapes special characters in the value to match literally (eg. `c++` becomes `c\\+\\+`)
  const escapedValue = targetValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // Matches exact `field:value` at word boundaries (eg. `label:bug` but not `label:bugfix`)
  const regex = new RegExp(`(?:^|\\s)(${field}:${escapedValue})(?=\\s|$)`, 'g')
  const newQuery = query.replaceAll(regex, '').replace(/^\s+/, '')
  const newValue = formatFieldValue(value)

  return `${newQuery.trim()} ${field}:${newValue}`
}

/**
 * Creates a URL for an issue query with the specified field and value
 * @param activeSearchQuery - The current query string to modify
 * @param scopedRepository - Whether to include the repository viewId in the URL
 * @param field - The field name to use in the query
 * @param value - The value for the field
 * @param additive - If true, preserves existing instances of the field; if false, replaces them
 * @returns The complete URL for the issue query
 * @example
 * // Replace behavior (additive = false)
 * createQueryFieldUrl('is:issue label:bug', true, 'label', 'enhancement', false)
 * // Returns: '/org/repo/issues?q=is%3Aissue%20label%3Aenhancement'
 *
 * // Additive behavior (additive = true)
 * createQueryFieldUrl('is:issue label:bug', false, 'label', 'enhancement', true)
 * // Returns: '/issues?q=is%3Aissue%20label%3Abug%20label%3Aenhancement'
 */
const createQueryFieldUrl = (
  activeSearchQuery: string,
  scopedRepository: boolean,
  field: keyof typeof QUERY_FIELDS,
  value: string,
  additive = false,
) => {
  const queryFunction = additive ? addToQuery : replaceInQuery
  return searchUrl({
    viewId: scopedRepository ? VIEW_IDS.repository : undefined,
    query: queryFunction(activeSearchQuery, field, value),
  })
}

/**
 * Creates a URL for an issue query, replacing any existing instances of the field
 * @param activeSearchQuery - The current query string to modify
 * @param scopedRepository - Whether to include the repository viewId in the URL
 * @param field - The field name to use in the query
 * @param value - The value for the field
 * @returns The complete URL for the issue query with replaced field
 * @example
 * createReplaceQueryFieldUrl('is:issue label:bug label:enhancement', true, 'label', 'feature')
 * // Returns: '/org/repo/issues?q=is%3Aissue%20label%3Afeature'
 */
export const createReplaceQueryFieldUrl = (
  activeSearchQuery: string,
  scopedRepository: boolean,
  field: keyof typeof QUERY_FIELDS,
  value: string,
) => createQueryFieldUrl(activeSearchQuery, scopedRepository, field, value, false)

/**
 * Creates a URL for an issue query, preserving existing instances of the field and adding the new one
 * @param activeSearchQuery - The current query string to modify
 * @param scopedRepository - Whether to include the repository viewId in the URL
 * @param field - The field name to use in the query
 * @param value - The value for the field
 * @returns The complete URL for the issue query with added field
 * @example
 * createAdditiveQueryFieldUrl('is:issue author:monalisa', false, 'is', 'blocked')
 * // Returns: '/issues?q=is%3Aissue%20author%3Amonalisa%20is%3Ablocked'
 */
export const createAdditiveQueryFieldUrl = (
  activeSearchQuery: string,
  scopedRepository: boolean,
  field: keyof typeof QUERY_FIELDS,
  value: string,
) => createQueryFieldUrl(activeSearchQuery, scopedRepository, field, value, true)
